import React, {useState, useCallback, forwardRef} from 'react';

export enum InputAppearance {
  Ghost = 'ghost',
  Solid = 'solid',
}

export enum InputShape {
  Pill = 'pill',
  Rounded = 'rounded',
}

export enum InputVariant {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
}

export enum EnhancerPosition {
  Start = 'start',
  End = 'end',
}

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  appearance?: InputAppearance;
  error?: boolean;
  endEnhancer?: React.ReactNode;
  shape?: InputShape;
  startEnhancer?: React.ReactNode;
  variant?: InputVariant;
  className?: string;
}

type InputProps = BaseInputProps;

const useFocusState = (onBlur?: React.FocusEventHandler<HTMLInputElement>, onFocus?: React.FocusEventHandler<HTMLInputElement>) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  return {
    isFocused,
    handleFocus,
    handleBlur,
  };
};

const sizeClasses = {
  large: 'py-4 text-lg',
  medium: 'py-3 text-base',
  small: 'py-2 text-sm',
};

const shapeClasses = {
  [InputShape.Rounded]: 'rounded-lg',
  [InputShape.Pill]: 'rounded-full',
};

const computeInputClasses = (
  appearance: NonNullable<InputProps['appearance']>,
  className: string | undefined,
  error: boolean,
  hasEndEnhancer: boolean,
  hasStartEnhancer: boolean,
  isFocused: boolean,
  shape: NonNullable<InputProps['shape']>,
  variant: NonNullable<InputProps['variant']>,
): string => {
  const baseClasses = [
    'w-full font-inherit transition-all duration-150 ease-in-out outline-none',
    shapeClasses[shape],
    'placeholder:text-gray-500 placeholder:opacity-100',
    sizeClasses[variant],
  ];

  const paddingClasses = [hasStartEnhancer ? 'pl-11' : 'pl-4', hasEndEnhancer ? 'pr-11' : 'pr-4'];

  const appearanceClasses = {
    [InputAppearance.Solid]: [
      'bg-white border',
      error
        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
        : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
    ],
    [InputAppearance.Ghost]: [
      'bg-transparent border-0 shadow-none focus:border-0 focus:ring-0',
      error ? 'focus:ring-2 focus:ring-red-200' : '',
    ],
  }[appearance];

  const stateClasses = [...appearanceClasses, className];

  return [...baseClasses, ...paddingClasses, ...stateClasses].filter(Boolean).join(' ');
};

interface EnhancerProps {
  children: React.ReactNode;
  position: EnhancerPosition;
}

const Enhancer: React.FC<EnhancerProps> = ({children, position}) => (
  <span
    className={`absolute z-10 flex items-center justify-center text-gray-400
      ${position === EnhancerPosition.Start ? 'left-3' : 'right-3'}
      top-1/2 -translate-y-1/2`}>
    {children}
  </span>
);

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      appearance = InputAppearance.Solid,
      className,
      disabled,
      endEnhancer,
      error = false,
      onBlur,
      onFocus,
      shape = InputShape.Rounded,
      startEnhancer,
      variant = InputVariant.Medium,
      ...props
    },
    ref,
  ) => {
    const {isFocused, handleFocus, handleBlur} = useFocusState(onBlur, onFocus);

    const inputClasses = computeInputClasses(appearance, className, error, !!endEnhancer, !!startEnhancer, isFocused, shape, variant);

    return (
      <div className={`w-full relative flex items-center ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        {startEnhancer && <Enhancer position={EnhancerPosition.Start}>{startEnhancer}</Enhancer>}

        <input
          ref={ref}
          {...props}
          className={inputClasses}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          aria-invalid={error}
        />

        {endEnhancer && <Enhancer position={EnhancerPosition.End}>{endEnhancer}</Enhancer>}
      </div>
    );
  },
);

Input.displayName = 'Input';
